// ============================================================================
// Send checkpoint — IndexedDB-backed recovery for the client-driven HoneyPacker
// blast. The real "Send now" loop runs in the browser, so a refresh / crash /
// closed tab mid-send would otherwise lose your place. We persist the in-flight
// job (full recipient list + cursor + tallies) after EVERY message, so the next
// time HoneyPacker opens we can resume from the exact next-unsent row instead of
// re-blasting from the top.
//
// IndexedDB (not localStorage) because a large list carries every CIO variable
// per row and blows past localStorage's ~5MB cap. Only ONE active checkpoint is
// kept (key 'active') — a second concurrent blast isn't a supported workflow.
// Every call is best-effort: storage failures never break a send.
// ============================================================================
const DB_NAME = 'hive'
const STORE = 'sendCheckpoint'
const KEY = 'active'

function openDb() {
  return new Promise((resolve, reject) => {
    let req
    try { req = indexedDB.open(DB_NAME, 1) } catch (e) { return reject(e) }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

// Save (overwrite) the single active checkpoint. Best-effort.
export async function saveCheckpoint(job) {
  try {
    const db = await openDb()
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).put(job, KEY)
      tx.oncomplete = () => { db.close(); resolve() }
      tx.onerror = () => { db.close(); reject(tx.error) }
      tx.onabort = () => { db.close(); reject(tx.error) }
    })
  } catch { /* best-effort — a send must not fail because storage is unavailable */ }
}

// Load the active checkpoint, or null if none / on error.
export async function loadCheckpoint() {
  try {
    const db = await openDb()
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly')
      const r = tx.objectStore(STORE).get(KEY)
      r.onsuccess = () => resolve(r.result || null)
      r.onerror = () => reject(r.error)
      tx.oncomplete = () => db.close()
    })
  } catch { return null }
}

// Remove the active checkpoint (called on natural completion or explicit discard).
export async function clearCheckpoint() {
  try {
    const db = await openDb()
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).delete(KEY)
      tx.oncomplete = () => { db.close(); resolve() }
      tx.onerror = () => { db.close(); reject(tx.error) }
    })
  } catch { /* best-effort */ }
}
